import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import PasswordStep from './PasswordStep';
import MobileVerificationStep from './MobileVerificationStep';
import AddressStep from './AddressStep';
import Footer from 'components/Footer/Footer';
import { AssetUrls } from 'config/assets';
import { tradeApi } from 'store/services/trade/tradeApi';
import { useReachedHomeMutation } from 'store/services/onboarding/onboardingApi';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { nextAfterSignup } from 'utils/play/signupRouting';
import type { RegisteredUser } from './PasswordStep';
import '../Login.css';

const OnboardingFlow = () => {
  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [reachedHome] = useReachedHomeMutation();
  const email = searchParams.get('email') || '';

  const handleNext = () => {
    setStep((s) => s + 1);
  };

  // A TradeMagic Play sign-up (Asana 1220000146037011, Decision 5, rulings 31
  // and 32) never sees the phone and address steps: it goes back to the
  // invite's name step or through the host username step. The funnel's
  // reached-home event is not fired for it.
  const handlePasswordDone = (user?: RegisteredUser) => {
    const next = nextAfterSignup({ onboardingLevel: user?.onboardingLevel, returnTo: ImportSessionStorage.getReturnTo() });
    if (next.kind === 'play') {
      ImportSessionStorage.clearReturnTo();
      navigate(next.to, { replace: true });
      return;
    }
    handleNext();
  };

  const handleComplete = async () => {
    // Invalidate trade packages cache to ensure fresh fetch with proper auth
    dispatch(tradeApi.util.invalidateTags(['TradePackages']));

    // Longer delay to ensure all cookies and auth state are properly set
    // Increased from 200ms to 500ms for cross-domain cookie reliability
    await new Promise(resolve => setTimeout(resolve, 500));

    // Funnel telemetry Step 5: reached home / import after onboarding (fire-and-forget;
    // backend no-ops after the first call).
    reachedHome().unwrap().catch(() => {});

    // Check for returnTo URL (e.g., from import flow)
    const returnTo = ImportSessionStorage.getAndClearReturnTo();
    navigate(returnTo || '/home');
  };

  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => {
      document.body.classList.remove('landing-page');
    };
  }, []);

  return (
    <div className="login-page-wrapper onboarding-page-wrapper">
      <div className="login-page-content">
        <img
          className="logo-main"
          src={AssetUrls.logos.black}
          alt="TradeMagic logo"
        />

        {step === 1 && <PasswordStep email={email} onNext={handlePasswordDone} />}
        {step === 2 && <MobileVerificationStep onNext={handleNext} />}
        {step === 3 && <AddressStep onNext={handleComplete} />}
      </div>

      <Footer />
    </div>
  );
};

export default OnboardingFlow;
